
import React, { useRef, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { incrementVideoCount, decrementVideoCount, setZoomedMediaId } from '../../../store/livekitSlice/livekitSlice';
import HandRankBadge from "../HandRankBadge/HandRankBadge";
import { useFloating, shift, flip, offset, autoUpdate } from "@floating-ui/react";
import { useTranslation } from 'react-i18next';
import { createPortal } from "react-dom";
import MediaContainer from '../MediaContainer/MediaContainer';
import { setOpenModal } from "../../../../../features/ControlPanel/store/controlPanelSlice.js";
import { setActiveTab } from "../../../../../features/ControlPanel/store/settings/settingsSlice.js";

export default function LocalVideo({ track, type, label, handRank }) {
  const videoRef = useRef(null);

  const { zoomedTrackId, conferenceMode, livekitMicActive } = useSelector((state) => state.livekit);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Floating video state  
  const [floatingVideo, setFloatingVideo] = useState(false);
  const [open, setOpen] = useState(false); // menu ouvert

  const { refs, floatingStyles } = useFloating({
    placement: "bottom-end",
    middleware: [
      offset(6),
      flip(),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
  });

  function useMobile() {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 480);

    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 700);
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isMobile;
  }

  const isMobile = useMobile();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !refs.reference.current?.contains(e.target) &&
        !refs.floating.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [refs.reference, refs.floating]);

  useEffect(() => {
    dispatch(incrementVideoCount());
    return () => {
      dispatch(decrementVideoCount());
    }
  }, [dispatch])

  // Attach video track to video element
  useEffect(() => {
    const videoEl = videoRef.current;
    if (videoEl && track) {
      videoEl.srcObject = new MediaStream([track]);
    }

    return () => {
      if (videoEl) {
        videoEl.srcObject = null;
      }
    }
  }, [track]);

  return (
    <>
      <MediaContainer
        mediaId={track.id}
        canZoom={true}
        canFullscreen={false} // LocalVideo never fullscreens
        canFloat={floatingVideo} // Parent controls floating state
        containerType="video"
      >
        {label !== 'Partage' &&
          <span className={`mic-status ${livekitMicActive ? 'mic-active' : 'mic-inactive'}`}>
            {livekitMicActive ?
              <span className="material-symbols-outlined">
                mic
              </span>
              :
              <span className="material-symbols-outlined">
                mic_off
              </span>
            }
          </span>
        }

        {type === 'localCamera' &&
          <div className={`video-control-panel ${open ? 'visible' : ''}`}>
            <div className='btn-video-control-panel menu-video-control-panel' onClick={(e) => { e.stopPropagation(); dispatch(setActiveTab("video")); dispatch(setOpenModal("settings")) }}>
              <span className="material-symbols-outlined">
                background_replace
              </span>
            </div>
            {(isMobile && conferenceMode) &&
              <div className='btn-video-control-panel menu-video-control-panel' ref={refs.setReference} onClick={(e) => {
                e.stopPropagation();
                setOpen(prev => !prev);
              }}>
                <span className="material-symbols-outlined">
                  more_vert
                </span>
              </div>
            }
          </div>
        }

        <video ref={videoRef} autoPlay playsInline muted className='video-track' />
        <HandRankBadge rank={handRank} />
        <div className={`video-track-label` + (label === 'Partage' ? ' video-track-label-with-icon' : '') +
          (label === 'Partage' && conferenceMode && zoomedTrackId == track.id ? ' video-track-label-with-icon-conference-mode' : '')}>
          {label === 'Partage' &&
            <>
              <span className="material-symbols-outlined">
                computer
              </span>
              {t("SHARE_TEXT")}
            </>
          }
          {label !== 'Partage' &&
            <>
              {label}
            </>
          }
        </div>
      </MediaContainer>

      {open &&
        createPortal(
          <div className='actions-menu-video' ref={refs.setFloating} style={
            isMobile
              ? { position: 'absolute', left: 0, right: 0, bottom: 0 }
              : floatingStyles
          }>
            <ul>
              {isMobile &&
                <li className='action-menu-item' onClick={() => {
                  setFloatingVideo(prev => !prev);
                  setOpen(prev => !prev);
                }}>
                  <span className="material-symbols-outlined">
                    drag_handle
                  </span>
                  {floatingVideo ? t("floating_mode_off") : t("floating_mode_on")}
                </li>
              }
            </ul>
          </div>,
          document.body
        )
      }
    </>
  );
};
